import { Suspense, lazy, useMemo, useState } from 'react'
import { cn } from '../../lib/cn'
import type { CourseGeometry, CourseHole, HoleGeometry, PlateImagery } from '../../lib/golfRound'
import { buildHoleMap, plateSource } from '../../lib/holeMap'
import { hasGeometry, holeIn, resolveTraceFinish, shotsForHole } from '../../lib/holeProjection'
import { EmptyPlate } from './EmptyPlate'
import { HolePlan } from './HolePlan'
import type { HolePlanPlayer } from './HolePlan'
import { holeControlLabel } from './holeMedia'
import { HoleMapBoundary } from './HoleMapBoundary'
import { TraceLegend } from './TraceLegend'
import { traceColor, traceDash } from './trace'
import { useRovingRadioGroup } from './useRovingRadioGroup'

/**
 * The live map, as its OWN CHUNK.
 *
 * `HoleMap` is the club's only importer of OpenLayers, and this is the line
 * that keeps it that way: a member reading a scorecard, a register or the
 * Grounds downloads none of it, and a member who opens a round page on an
 * unmapped course — half the club's courses — downloads none of it either.
 * The paper plan below is what fills the frame until the chunk lands, so the
 * hole is drawn from the first paint rather than after a round trip.
 *
 * `HoleMapBoundary` is imported STATICALLY beside it, and must be: a boundary
 * can only catch what its children throw, and putting it inside the lazy
 * module would put `ol` back in this chunk through it.
 */
const HoleMap = lazy(() => import('./HoleMap'))

/**
 * A player as every trace presentation takes them: what the plan draws, plus
 * the card the legend quotes.
 *
 * Declared HERE and imported by the gallery and the inline strip, so the three
 * presentations are three layouts over one shape rather than three shapes.
 */
export type HoleTracePlayer = HolePlanPlayer

export interface HoleTraceStageProps {
  /** The hole numbers this round PLAYED, in play order. The rail's contents. */
  holeNumbers: readonly number[]
  /** The course's card — par and stroke index for the stage's own heading. */
  courseHoles: readonly CourseHole[]
  players: readonly HoleTracePlayer[]
  /** The course's traced geometry, or null where nobody has traced it. */
  geometry: CourseGeometry | null
  courseName: string
  /**
   * Which hole the stage opens on. Ignored — like an absent one — where
   * nobody tracked a shot on it; the default is the first hole anybody did.
   */
  initialHole?: number | null
  /**
   * The hole on stage, CONTROLLED — for a caller that owns the choice (the
   * gallery's grid is one). Omit it and the stage keeps its own.
   */
  hole?: number | null
  /** Told whenever the member chooses another hole — for a `?hole=N` in the URL. */
  onHoleChange?: (holeNumber: number) => void
  /** The rail is the stage's own hole chooser; hidden where the caller has one. */
  showRail?: boolean
  /** Ready media per hole, announced and marked on hole controls. */
  mediaCounts?: ReadonlyMap<number, number>
  className?: string
}

/**
 * ONE HOLE ON STAGE, with a 1-18 rail beneath it and a toggle per player —
 * the presentation the maintainer chose (decision record §2, 2026-08-22 —
 * `../../../../../../docs/fireside/club-web-decisions.md`) and the
 * one the round page ships.
 *
 * ## The three states that are not a drawing
 *
 * | State | What renders |
 * |---|---|
 * | the round has no shots at all | "Scores only." — a plate, and NO svg |
 * | the course (or this hole) is untraced | one honest line, and NO svg |
 * | some players traced, some not | the plan, and a legend of only the traced |
 *
 * The first two are the CALLER-side rows of `HolePlan`'s degradation matrix,
 * which is why they live here: an empty frame would still spend the height and
 * still announce itself to a screen reader. Seven of the club's fifteen
 * courses are untraced, so the untraced line is an ordinary afternoon rather
 * than an error.
 *
 * ## Yardages appear only when one player is left on
 *
 * Four players' leg yardages on one hole is twelve to sixteen numbers over
 * each other and legible as none of them. So `HolePlan`'s focused mode is
 * spent exactly when the toggles have narrowed the hole to a single player;
 * the traces themselves always draw, because telling a fourball apart is what
 * the tone, the dash and the initial are for.
 *
 * ## The rail is a RADIOGROUP
 *
 * Exactly one of eighteen is chosen and choosing re-draws what is already on
 * the page — which is a radiogroup, not a tablist and not a row of buttons.
 * It therefore takes ONE tab stop with a roving tabindex (the same HOOK, and
 * the same reasoning, as `SegmentedControl`); eighteen tab stops would
 * make a keyboard user walk the whole card to get past the picture. The player
 * toggles beside it are the other thing: independent on/off switches, so they
 * are `aria-pressed` buttons and any number may be on at once.
 *
 * ## A hole nobody TRACKED is greyed out in place
 *
 * Tracking stops when a phone goes back in a pocket, so most rounds carry
 * shots on some holes and none on the rest. The rail keeps every hole the
 * round played — it is the card's shape, and a chip quietly missing from it
 * would read as a hole nobody played — and greys the untracked ones: the chip
 * is `disabled`, the arrows step over it, and its name says why rather than
 * leaving the grey to be seen. The stage opens on the first hole with shots
 * on it, because the alternative is opening on an empty frame.
 */
export function HoleTraceStage({
  holeNumbers,
  courseHoles,
  players,
  geometry,
  courseName,
  initialHole = null,
  hole: controlledHole = null,
  onHoleChange,
  showRail = true,
  mediaCounts,
  className,
}: HoleTraceStageProps) {
  // The holes anybody TRACKED, over every player rather than the shown ones:
  // a toggle hides a line, it does not un-play a hole, so a hole stays
  // pickable while the only member who traced it is switched off.
  const playedHoles = holeNumbers.filter((number) =>
    players.some((player) => shotsForHole(player.shots, number).length > 0),
  )

  const opening = openingHole(initialHole, playedHoles, holeNumbers)
  const [chosen, setChosen] = useState(opening)
  const [hidden, setHidden] = useState<readonly string[]>([])

  // Derived rather than synced: a round whose holes changed under the stage
  // falls back to its opening hole instead of drawing one it never played.
  // Tested against `holeNumbers`, NOT `playedHoles`: a controlled caller owns
  // its own choice — the gallery's grid is sized by the geometry, and a hole
  // it can reach must arrive on stage with its own honest line.
  const requested = controlledHole ?? chosen
  const hole = holeNumbers.includes(requested) ? requested : opening

  const choose = (holeNumber: number) => {
    setChosen(holeNumber)
    onHoleChange?.(holeNumber)
  }

  // The rail's keyboard half — see `useRovingRadioGroup`, which the gallery's
  // grid and the register's segmented control share. Sized from `playedHoles`
  // and NOT from the chips, for the gallery's own reason: an index that could
  // point at a chip nobody can choose strands the arrow keys on it.
  const rail = useRovingRadioGroup(playedHoles.length, (index) => {
    const holeNumber = playedHoles[index]
    if (holeNumber !== undefined) choose(holeNumber)
  })

  // ── What the live map draws, and WHEN that changes ────────────────────────
  //
  // Above the scores-only short-circuit below because these are hooks, and a
  // hook may not sit behind a return.

  const shown = useMemo(
    () => players.filter((player) => !hidden.includes(player.id)),
    [players, hidden],
  )
  const drawable = hasGeometry(geometry, hole)
  const holeGeometry = holeIn(geometry, hole)
  const scale = geometry?.scale ?? null

  /**
   * The imagery this client can actually draw, or null.
   *
   * There is no key in this decision and no key anywhere behind it: the plate
   * is New York State's free public survey. Null is the ORDINARY state — a
   * payload from before the club served a plate, or one naming a service shape
   * this client cannot build — and it draws the paper plan without saying
   * anything is missing. Narrowed to non-null here so the map branch below
   * cannot be reached with a plate nothing can draw.
   */
  const plate = geometry?.plate && plateSource(geometry.plate) ? geometry.plate : null

  /**
   * The map's whole input, MEMOISED — and the memo is the point.
   *
   * `HoleMap` builds its OpenLayers map once and redraws in place, keyed on
   * this object's identity. A new one on every render would tear the canvas
   * down, re-fetch tiles and throw away a reader's pan and zoom every time
   * anything on the page moved; one that never changed would leave a switched
   * off player's line on the map. So it changes when — and only when — the
   * hole, the course's scale or WHO IS ON STAGE changes. Toggling a player
   * produces a new spec; re-rendering the legend beside it does not.
   *
   * The plate rides along so the markup below needs one condition rather than
   * two, and so the pair is narrowed together.
   */
  const map = useMemo(
    () =>
      plate && holeGeometry && scale
        ? { plate, spec: buildHoleMap({ hole: holeGeometry, scale, players: shown }) }
        : null,
    [plate, holeGeometry, scale, shown],
  )

  // A round kept without shot tracking is a complete round — the card is the
  // record and there is simply nothing to draw. Said once, for the whole
  // section, rather than eighteen times over eighteen empty frames.
  if (!players.some((player) => player.shots.length > 0)) {
    return (
      <EmptyPlate
        className={className}
        title="Scores only."
        description="This round was kept without shot tracking. Every stroke is on the card above; there is no trace to draw."
      />
    )
  }

  const traced = shown.filter((player) => shotsForHole(player.shots, hole).length > 0)
  const card = courseHoles.find((courseHole) => courseHole.number === hole)
  const traceNote = finishNote(traced, hole, holeGeometry)
  // Exactly one player left on ⇒ their yardages. See the docstring.
  const focusedPlayerId = shown.length === 1 ? (shown[0]?.id ?? null) : null

  /**
   * Everything a drawn hole needs, or null where there is nothing to draw.
   *
   * One object rather than three consts because all three want the same
   * narrowing — a hole this course was traced on — and TypeScript cannot carry
   * that from `drawable` into the markup below on its own.
   *
   * `plan` is today's picture, and now ALSO the map's placeholder and its
   * fallback: built once and spent up to three times.
   */
  const drawn =
    drawable && holeGeometry && geometry
      ? {
          plan: (
            <HolePlan
              hole={holeGeometry}
              scale={geometry.scale}
              par={card?.par ?? null}
              players={shown}
              focusedPlayerId={focusedPlayerId}
              className="mt-3 rounded-club border border-sage-soft bg-paper"
              width={640}
              height={420}
            />
          ),
          credit: creditLine(geometry, plate),
          map,
        }
      : null

  return (
    <div className={cn('flex flex-wrap items-start gap-6', className)} data-hole-trace-stage={hole}>
      <div className="min-w-0 flex-[1_1_460px] rounded-club-lg border border-sage-soft bg-parchment p-5">
        <div className="flex flex-wrap items-baseline gap-x-3.5 gap-y-1">
          <h3 className="font-display text-[22px] font-medium text-pine">Hole {hole}</h3>
          {card && <span className="text-[12px] text-ink-soft">{holeLine(card)}</span>}
        </div>

        {drawn ? (
          <>
            {drawn.map ? (
              <div data-hole-map-slot="">
                {/* The boundary OUTSIDE the Suspense: a map that throws at
                    runtime falls back to the plan, and a member keeps the
                    hole rather than losing the whole round page. */}
                <HoleMapBoundary fallback={drawn.plan}>
                  <Suspense fallback={drawn.plan}>
                    <HoleMap
                      // Keyed by hole, so choosing another one builds its map
                      // rather than re-aiming this one at a different course.
                      key={hole}
                      spec={drawn.map.spec}
                      plate={drawn.map.plate}
                      focusedPlayerId={focusedPlayerId}
                      title={`Hole ${hole}, ${courseName}`}
                      credit={drawn.credit}
                    />
                  </Suspense>
                </HoleMapBoundary>
              </div>
            ) : (
              drawn.plan
            )}
            {/* ODbL: attribution is the whole licence obligation for a produced
                work, and the club sends the credit line WITH the coordinates
                rather than leaving a client to remember it. The imagery credit
                joins it, crediting the survey, and prints beside
                the plate, on the page, where a screen reader finds it — the map
                draws no attribution control of its own. */}
            <p className="mt-2 text-[10.5px] text-ink-soft">{drawn.credit}</p>
          </>
        ) : (
          <p className="mt-3 rounded-club border border-dashed border-brass/55 bg-paper px-4 py-5 text-[13px] text-ink-soft" data-unmapped-note="">
            {unmappedNote(geometry, hole, courseName)}
          </p>
        )}

        <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Whose traces are drawn">
          {players.map((player) => {
            const on = !hidden.includes(player.id)
            return (
              <button
                key={player.id}
                type="button"
                aria-pressed={on}
                data-trace-toggle={player.id}
                onClick={() =>
                  setHidden((previous) =>
                    previous.includes(player.id)
                      ? previous.filter((id) => id !== player.id)
                      : [...previous, player.id],
                  )
                }
                className={cn(
                  'flex items-center gap-2 rounded-club border px-2.5 py-1.5 text-[12.5px]',
                  on ? 'border-pine bg-pine font-semibold text-paper' : 'border-sage bg-parchment text-ink-soft',
                )}
              >
                <Swatch position={player.position} />
                {player.displayName}
                {/* The pressed state must not be carried by ground colour
                    alone — a reader who sees none of it gets the word. */}
                {!on && (
                  <span className="text-[8.5px] font-semibold uppercase tracking-[0.14em] text-brass-deep">
                    Off
                  </span>
                )}
              </button>
            )
          })}
        </div>

        <p className="mt-2.5 text-[11.5px] text-ink-soft">
          Every number drawn here is also on the card above — the plan is the story, never the record.
          {' '}
          <span role="status" aria-live="polite" aria-atomic="true" data-trace-finish-status="">
            {traceNote}
          </span>{' '}
          Putts stay on the card.
        </p>
      </div>

      <aside className="flex-[1_1_200px] rounded-club-lg border border-sage-soft bg-parchment px-4 py-3.5">
        <span className="block text-[10.5px] font-semibold uppercase tracking-[0.2em] text-brass-deep">
          This hole, by the book
        </span>
        {traced.length > 0 ? (
          <TraceLegend
            className="mt-2.5 flex-col items-start gap-y-2"
            label={`Shot traces, hole ${hole}`}
            players={traced.map((player) => ({
              id: player.id,
              position: player.position,
              displayName: player.displayName,
              detail: legendDetail(player, hole, card?.par),
            }))}
          />
        ) : (
          <p className="mt-2 text-[12px] text-ink-soft">No trace is drawn on this hole.</p>
        )}
      </aside>

      {showRail && (
        <div
          role="radiogroup"
          aria-label="Choose a hole"
          // Wraps rather than scrolls: eighteen chips on two rows is a working
          // control; eighteen chips in a sideways scroller is a hidden one.
          className="flex basis-full flex-wrap gap-1.5"
          data-hole-rail=""
        >
          {holeNumbers.map((number) => {
            const railCard = courseHoles.find((courseHole) => courseHole.number === number)
            const mediaCount = mediaCounts?.get(number) ?? 0
            const label = holeControlLabel(number, railCard, mediaCount)
            const index = playedHoles.indexOf(number)
            // A hole nobody tracked stays IN PLACE, greyed: the rail is the
            // card's shape, and a chip missing from it would read as a hole
            // that was never played rather than one nobody reached for their
            // phone on. Dashed and unfilled says where the tracking stopped —
            // in SAGE, not sage-soft: the two fills are 1.09:1 apart, so the
            // dash is the whole visible difference, and sage-soft draws it at
            // 1.17:1 against the chip it outlines. Sage is 2.32:1, which is
            // quiet enough to read as inactive and visible enough to read at
            // all. (The chip is genuinely disabled, so 1.4.11 exempts it; a
            // member scanning eighteen chips still has to see which ones they
            // can click.)
            if (index === -1) {
              return (
                <button
                  key={number}
                  type="button"
                  role="radio"
                  aria-checked={false}
                  aria-disabled="true"
                  aria-label={`${label} — no shots tracked`}
                  disabled
                  tabIndex={-1}
                  data-hole-chip={number}
                  data-hole-chip-disabled=""
                  data-media-count={mediaCount || undefined}
                  className="relative grid h-9 w-10 place-items-center rounded-club border border-dashed border-sage bg-paper text-[13px] tabular-nums leading-none text-ink-soft"
                >
                  <span>
                    {number}
                    {railCard && (
                      <small className="block text-[8px] leading-none text-ink-soft">
                        {railCard.par}
                      </small>
                    )}
                  </span>
                  {mediaCount > 0 && (
                    <span
                      className="absolute right-1 top-0.5 text-[9px] leading-none text-fairway"
                      aria-hidden="true"
                    >
                      ●
                    </span>
                  )}
                </button>
              )
            }

            const checked = number === hole
            return (
              <button
                key={number}
                ref={rail.optionRef(index)}
                type="button"
                role="radio"
                aria-checked={checked}
                aria-label={label}
                tabIndex={checked ? 0 : -1}
                onClick={() => choose(number)}
                onKeyDown={(event) => rail.onKeyDown(event, index)}
                data-hole-chip={number}
                data-media-count={mediaCount || undefined}
                className={cn(
                  'relative grid h-9 w-10 place-items-center rounded-club border text-[13px] tabular-nums leading-none',
                  checked ? 'border-pine bg-pine text-paper' : 'border-sage-soft bg-parchment text-ink',
                )}
              >
                <span>
                  {number}
                  {railCard && (
                    <small className={cn('block text-[8px] leading-none', checked ? 'text-sage' : 'text-ink-soft')}>
                      {railCard.par}
                    </small>
                  )}
                </span>
                {mediaCount > 0 && (
                  <span
                    className={cn(
                      'absolute right-1 top-0.5 text-[9px] leading-none',
                      checked ? 'text-gilt' : 'text-fairway',
                    )}
                    aria-hidden="true"
                  >
                    ●
                  </span>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function finishNote(
  players: readonly HoleTracePlayer[],
  holeNumber: number,
  hole: HoleGeometry | undefined,
): string {
  const finishes = players.map((player) => {
    const shots = shotsForHole(player.shots, holeNumber)
    const score = player.scores?.find((entry) => entry.hole_number === holeNumber)
    return resolveTraceFinish(shots, hole, score).finish
  })

  if (finishes.length === 0) return 'No trace is drawn on this hole.'
  if (finishes.every((finish) => finish === 'recorded')) {
    return finishes.length === 1 ? 'The trace reaches the green.' : 'The traces reach the green.'
  }
  if (finishes.every((finish) => finish === 'mapped-center')) {
    return finishes.length === 1
      ? 'The trace reaches the mapped green center.'
      : 'The traces reach the mapped green center.'
  }
  if (finishes.every((finish) => finish === 'partial')) {
    return 'Tracking stops at the last recorded position.'
  }
  if (!finishes.includes('mapped-center')) {
    return 'Complete traces reach the green; tracking stops at the last recorded position for the others.'
  }

  const recorded = finishes.filter((finish) => finish === 'recorded').length
  const mapped = finishes.filter((finish) => finish === 'mapped-center').length
  const partial = finishes.filter((finish) => finish === 'partial').length
  const clauses: string[] = []
  if (recorded > 0) {
    clauses.push(recorded === 1 ? 'one trace reaches the green' : 'recorded traces reach the green')
  }
  if (mapped > 0) {
    clauses.push(
      mapped === 1
        ? 'one trace reaches the mapped green center'
        : 'mapped-center traces reach the mapped green center',
    )
  }
  if (partial > 0) {
    clauses.push(
      partial === 1
        ? 'tracking stops at the last recorded position for one trace'
        : 'tracking stops at the last recorded position for the other traces',
    )
  }

  const sentence = clauses.join('; ')
  return `${sentence[0]!.toUpperCase()}${sentence.slice(1)}.`
}

/**
 * A player's line in the toggle, on either ground.
 *
 * The parchment backing is not decoration: the design card draws the pressed
 * chip on pine and the first seat's tone IS pine, so the card's own swatch
 * disappears on the chip it is meant to label. The backing keeps all four
 * tones legible in both states without inventing a fifth colour.
 */
function Swatch({ position }: { position: number }) {
  return (
    <svg width="22" height="10" viewBox="0 0 22 10" aria-hidden="true" className="block shrink-0">
      <rect width="22" height="10" rx="2" fill="var(--color-parchment)" />
      <line
        x1="2"
        y1="5"
        x2="20"
        y2="5"
        stroke={traceColor(position)}
        strokeWidth="2.4"
        strokeDasharray={traceDash(position)}
        strokeLinecap="round"
      />
    </svg>
  )
}

/**
 * The hole the stage opens on: the caller's, if somebody TRACKED it.
 *
 * `holeNumbers` is the last resort rather than the first, and it is not
 * decoration: a bare `1` would open a back nine on a hole that round never
 * played, which is the one number the rail cannot show as chosen.
 */
function openingHole(
  initialHole: number | null,
  playedHoles: readonly number[],
  holeNumbers: readonly number[],
): number {
  if (initialHole !== null && playedHoles.includes(initialHole)) return initialHole
  return playedHoles[0] ?? holeNumbers[0] ?? 1
}

/**
 * What is printed beside the picture — one line carrying both obligations.
 *
 * The ODbL credit is always there: the coordinates came from OpenStreetMap
 * whether or not there is a photograph under them. The imagery's credit joins
 * it only when there is imagery on screen, and LEADS, because that is what a
 * member is looking at. New York publishes no terms for its survey and asks
 * for nothing — the club credits the agency anyway.
 */
function creditLine(geometry: CourseGeometry, plate: PlateImagery | null): string {
  if (plate === null) return geometry.attribution

  return `Imagery: ${plate.attribution} · ${geometry.attribution}`
}

function holeLine(card: CourseHole): string {
  const parts = [`Par ${card.par}`]
  if (card.yardage !== null) parts.push(`${card.yardage} yards`)
  parts.push(`stroke index ${card.stroke_index}`)
  return parts.join(' · ')
}

/**
 * The honest line that stands where a plan would be.
 *
 * Two sentences rather than one, because they say different things: nobody has
 * traced this ground, AND the round itself is complete. A member whose course
 * is untraced has lost a picture, not a record.
 */
function unmappedNote(geometry: CourseGeometry | null, hole: number, courseName: string): string {
  if (geometry === null) {
    return `The club has not mapped ${courseName}. The card is complete; the drawings are not.`
  }
  return `The club has not mapped hole ${hole} at ${courseName}. The card is complete; the drawing is not.`
}

/** What this hole cost the player, as the card says it — never as the plan guesses. */
function legendDetail(
  player: HoleTracePlayer,
  hole: number,
  par: number | undefined,
): string | undefined {
  const strokes = player.scores?.find((score) => score.hole_number === hole)?.strokes
  if (strokes === null || strokes === undefined) return undefined
  if (par === undefined) return String(strokes)
  return `${strokes} · ${shapeName(strokes - par)}`
}

function shapeName(toPar: number): string {
  if (toPar <= -3) return 'albatross'
  if (toPar === -2) return 'eagle'
  if (toPar === -1) return 'birdie'
  if (toPar === 0) return 'par'
  if (toPar === 1) return 'bogey'
  if (toPar === 2) return 'double'
  return `+${toPar}`
}
